import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useLanguage } from '@/contexts/LanguageContext';

interface StateTime {
  state: string;
  medianDays: number;
}

const stateKeys: Record<string, string> = {
  APPLIED_NEW: 'dashboard.stateNew',
  PIPELINE_RUNNING: 'dashboard.stateProcessing',
  AWAITING_DECISION: 'dashboard.stateAwaitingDecision',
  PIPELINE_ERROR: 'dashboard.stateReviewRequired',
  REJECTED: 'dashboard.stateRejected',
  HIRED: 'dashboard.stateHired',
  OFFER_DECLINED: 'dashboard.stateOfferDeclined',
  WITHDRAWN: 'dashboard.stateWithdrawn',
};

const stateOrder = [
  'APPLIED_NEW',
  'PIPELINE_RUNNING',
  'AWAITING_DECISION',
  'PIPELINE_ERROR',
];

function getSemaphore(state: string, days: number): 'green' | 'yellow' | 'red' {
  const thresholds: Record<string, [number, number]> = {
    APPLIED_NEW: [1, 3],
    PIPELINE_RUNNING: [0.5, 2],
    AWAITING_DECISION: [3, 7],
    PIPELINE_ERROR: [1, 3],
  };
  const [ok, warn] = thresholds[state] || [2, 5];
  if (days <= ok) return 'green';
  if (days <= warn) return 'yellow';
  return 'red';
}

const semColors = {
  green: {
    dot: 'bg-success',
    line: 'bg-success/30',
    text: 'text-success',
    bg: 'bg-success/5',
    border: 'border-success/20',
  },
  yellow: {
    dot: 'bg-warning',
    line: 'bg-warning/30',
    text: 'text-warning',
    bg: 'bg-warning/5',
    border: 'border-warning/20',
  },
  red: {
    dot: 'bg-destructive',
    line: 'bg-destructive/30',
    text: 'text-destructive',
    bg: 'bg-destructive/5',
    border: 'border-destructive/20',
  },
};

export const TimeInStateChart = ({ data }: { data: StateTime[] }) => {
  const { t } = useLanguage();

  if (data.length === 0) {
    return (
      <Card className="border-border/50 min-w-0 max-w-full overflow-hidden">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">{t('dashboard.medianTimePerState')}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-8">{t('dashboard.noHistoryData')}</p>
        </CardContent>
      </Card>
    );
  }

  const dataMap = new Map(data.map(d => [d.state, d.medianDays]));
  const maxDays = Math.max(...data.map(d => d.medianDays), 1);

  const ordered = [
    ...stateOrder.filter(s => dataMap.has(s)),
    ...data.filter(d => !stateOrder.includes(d.state)).map(d => d.state),
  ];

  const totalDays = ordered.reduce((sum, s) => sum + (dataMap.get(s) || 0), 0);

  return (
    <Card className="border-border/50 min-w-0 max-w-full overflow-hidden">
      <CardHeader className="pb-3">
        <div className="flex min-w-0 items-center justify-between gap-2">
          <CardTitle className="min-w-0 text-sm font-medium">{t('dashboard.medianTimePerState')}</CardTitle>
          <div className="flex shrink-0 items-baseline gap-1.5">
            <span className="text-xs text-muted-foreground">Total</span>
            <span className="text-lg font-bold">{totalDays.toFixed(1)}d</span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="min-w-0">
        <div className="space-y-4">
          {ordered.map((state, i) => {
            const days = dataMap.get(state) || 0;
            const sem = getSemaphore(state, days);
            const colors = semColors[sem];
            const isLast = i === ordered.length - 1;
            const barWidth = Math.max((days / maxDays) * 100, 8);

            return (
              <div key={state} className="flex min-w-0 items-start gap-3">
                <div className="flex shrink-0 flex-col items-center pt-1">
                  <div className={`h-3 w-3 rounded-full ${colors.dot} ring-2 ring-background`} />
                  {!isLast && (
                    <div className={`mt-1 w-0.5 flex-1 ${colors.line}`} style={{ minHeight: '28px' }} />
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <div className="mb-1.5 flex min-w-0 items-center justify-between gap-2">
                    <span className="min-w-0 truncate text-xs font-medium text-foreground">
                      {t(stateKeys[state] || state)}
                    </span>
                    <span className={`shrink-0 text-sm font-bold tabular-nums ${colors.text}`}>
                      {days}d
                    </span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-muted">
                    <div
                      className={`h-full rounded-full ${colors.dot} transition-all duration-500`}
                      style={{ width: `${barWidth}%` }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
